import type { PropertyPublic, StoreSetupReuse, StoreSetupStatus } from "@townsy/contracts";
import { usePropertyQuery } from "../../api/queries";
import { isRewardsUnavailable } from "../../api/rewards";
import { useStoreOffersQuery } from "../../api/storeOffers";
import { useStoreSetupQuery } from "../../api/storeSetup";
import { storeReuseQuestion, storeStatusCopy } from "../../storeSetup/storeSetupModel";
import { useTownStore } from "../../state/store";
import { canPickStoreTheme } from "../storeThemeLogic";
import { StatusBanner } from "./StoreSetupDialog";

/**
 * The ways into "Set up your store" / "Edit your store" (ADR-0020) besides the purchase toast and the checkout: the
 * owner's store panel (first, prominent), the receipt, and the directory's "My stores". Offered only to the current
 * owner (their own property read lists `edit_interior`, the server's current-owner rule); the server re-checks.
 */

export const STORE_SETUP_WHAT = "Name, logo, website, description, the sign inside and the interior: all on one screen.";

/** The store panel's first section for its owner: the one plain status and the button. */
export function StoreSetupPanelSection({ item }: { item: PropertyPublic }) {
  const signedIn = useTownStore((s) => Boolean(s.bootstrap?.session.userId));
  const own = usePropertyQuery(signedIn ? item.id : null);
  const isOwner = canPickStoreTheme(own.data, signedIn);
  const setup = useStoreSetupQuery(isOwner ? item.id : null);
  const live = setup.data?.status.state === "live";
  // ADR-0027: offers are built on the live store, so "Add an offer" sits next to "Edit your store" once it is live.
  const offers = useStoreOffersQuery(isOwner && live ? item.id : null);
  const open = useTownStore((s) => s.openStoreSetup);
  const openOffers = useTownStore((s) => s.openOfferSetup);
  if (!isOwner) return null;
  const offersOn = live && !(offers.error && isRewardsUnavailable(offers.error));
  return (
    <StoreSetupPanelView
      status={setup.data?.status ?? null}
      onOpen={() => open(item.id, "panel")}
      offers={offersOn ? { hasOffers: Boolean(offers.data?.campaign), onOpen: () => openOffers(item.id, "store") } : null}
      reuse={setup.data?.reuse ?? null}
      onReuse={() => open(item.id, "panel", { reuse: true })}
    />
  );
}

/** The section itself (server-rendered in unit tests). */
export function StoreSetupPanelView({
  status,
  onOpen,
  offers = null,
  reuse = null,
  onReuse,
}: {
  status: Pick<StoreSetupStatus, "state" | "reason" | "categoryPolicy" | "previousSignShowing"> | null;
  onOpen(): void;
  /** ADR-0027: "Add an offer" / "Edit offers" next to "Edit your store" (null: not offered). */
  offers?: { hasOffers: boolean; onOpen(): void } | null;
  /** ADR-0031: the owner's own design from an earlier store (the server sends it while this store shows none of theirs). */
  reuse?: Pick<StoreSetupReuse, "businessName" | "fromAddress" | "offers"> | null;
  onReuse?(): void;
}) {
  const fresh = status?.state === "not_set_up";
  const offerReuse = Boolean(reuse && onReuse);
  return (
    <div className="section ss-panel" data-testid="store-setup-section">
      <div className="section-title">Your store</div>
      {status ? <StatusBanner status={status} copy={storeStatusCopy(status, "panel")} compact /> : <p className="small muted" style={{ margin: 0 }}>Checking your store…</p>}
      {reuse && offerReuse ? (
        <>
          <p className="small" style={{ margin: 0 }} data-testid="store-setup-panel-reuse">
            <strong>{storeReuseQuestion(reuse)}</strong> One press fills in your store screen; it was already checked, so Save puts it up right away.
            {reuse.offers > 0 ? " Then your offers from there." : ""}
          </p>
          <button type="button" className="btn btn-primary btn-lg btn-block ss-panel-btn" onClick={() => onReuse?.()} data-testid="store-setup-panel-reuse-button">
            Re-use your store design
          </button>
        </>
      ) : null}
      <button type="button" className={offerReuse ? "btn btn-block ss-panel-btn" : "btn btn-primary btn-lg btn-block ss-panel-btn"} onClick={onOpen} data-testid="edit-your-store">
        {fresh ? "Set up your store" : "Edit your store"}
      </button>
      <span className="hint">{STORE_SETUP_WHAT}</span>
      {offers ? (
        <button type="button" className="btn btn-block ss-panel-offers" onClick={offers.onOpen} data-testid="store-setup-add-offer">
          {offers.hasOffers ? "Edit offers" : "Add an offer"}
        </button>
      ) : null}
    </div>
  );
}

/** The receipt's first call to action after buying a store (above the interior callout). */
export function ReceiptStoreSetupCallout({ blocked, reason, onOpen }: { blocked: boolean; reason: string | null; onOpen(): void }) {
  return (
    <section className="receipt-setup" aria-labelledby="receipt-setup-title" data-testid="receipt-store-setup">
      <div className="receipt-setup-mark" aria-hidden="true">
        <span />
      </div>
      <div className="stack" style={{ gap: 6, minWidth: 0 }}>
        <h2 id="receipt-setup-title" style={{ margin: 0 }}>
          Set up your store
        </h2>
        <p className="small" style={{ margin: 0 }}>
          Put your business name, logo, website and description on your new store, and write the sign inside, all on one screen.
        </p>
        <div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={blocked}
            aria-describedby={blocked ? "receipt-setup-reason" : undefined}
            onClick={onOpen}
            data-testid="receipt-store-setup-button"
          >
            Set up your store
          </button>
        </div>
        {blocked && reason ? (
          <span id="receipt-setup-reason" className="hint">
            {reason}
          </span>
        ) : null}
      </div>
    </section>
  );
}

/** "Edit your store" on the directory's "My stores" (and the no-3D fallback). */
export function MyStoreSetupButton({ propertyId }: { propertyId: string }) {
  const open = useTownStore((s) => s.openStoreSetup);
  return (
    <button type="button" className="btn btn-primary btn-sm" onClick={() => open(propertyId, "directory")} data-testid={`my-store-edit-${propertyId}`}>
      Edit your store
    </button>
  );
}
